import { FC, useState, useContext, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { InstanceContext } from "../../context/InstanceContext";
import {
  quoteSeatsAsync,
  checkoutSeatAsync,
  formatSeatPriceLabel,
  ISeatQuote,
} from "../../services/crystal";
import styles from "./index.module.css";

type Interval = "monthly" | "yearly";

const SUBSCRIPTION_OPTIONS: { value: Interval; label: string }[] = [
  { value: "yearly", label: "Yearly" },
  { value: "monthly", label: "Monthly" },
];

// Friendly labels for known seat types; falls back to the raw seat_type.
const SEAT_TYPE_LABELS: Record<string, string> = {
  crystal_normal_tier1: "MSP Basic",
  crystal_normal_tier2: "MSP 30-Day Flows",
};

function seatTypeLabel(seatType: string): string {
  return SEAT_TYPE_LABELS[seatType] ?? seatType;
}

const Crystal: FC = () => {
  const authInfo = useContext(AuthContext);
  const { instancesWithRole } = useContext(InstanceContext);

  // deep-link support: an external link can pass ?type=<seat_type>&subscription=<monthly|yearly>
  // to pre-fill the Crystal type / billing interval selectors.
  const [searchParams] = useSearchParams();
  const requestedSeatType = searchParams.get("type");
  const requestedInterval = searchParams.get("subscription");

  // A crystal seat binds to an MSP instance the customer owns; members can't buy.
  const ownerInstances = useMemo(
    () => instancesWithRole.filter((it) => it.role === "owner"),
    [instancesWithRole]
  );

  // Default to the sole owned instance; when several exist the customer picks.
  const [mspId, setMspId] = useState("");
  useEffect(() => {
    setMspId((prev) =>
      ownerInstances.some((it) => it.id === prev)
        ? prev
        : ownerInstances[0]?.id ?? ""
    );
  }, [ownerInstances]);

  const [seats, setSeats] = useState<ISeatQuote[]>([]);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  const [seatType, setSeatType] = useState("");
  const [interval, setInterval] = useState<Interval>(
    SUBSCRIPTION_OPTIONS.some((opt) => opt.value === requestedInterval)
      ? (requestedInterval as Interval)
      : SUBSCRIPTION_OPTIONS[0].value
  );

  const [submitting, setSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // load the price list. Auth is required; `request` redirects to login when no
  // token is present, so viewing this page implies signing in first.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingQuote(true);
        setQuoteError(null);
        const { seats } = await quoteSeatsAsync();
        if (cancelled) return;
        const usable = seats.filter((s) => s.prices?.length);
        setSeats(usable);
        setSeatType((prev) => {
          if (usable.some((s) => s.seat_type === prev)) return prev;
          if (usable.some((s) => s.seat_type === requestedSeatType)) {
            return requestedSeatType as string;
          }
          return usable[0]?.seat_type ?? "";
        });
      } catch (err) {
        if (!cancelled) {
          setQuoteError(
            (err as Error).message || "Failed to load Crystal plans."
          );
        }
      } finally {
        if (!cancelled) setLoadingQuote(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authInfo.id_token]);

  const selectedSeat = useMemo(
    () => seats.find((s) => s.seat_type === seatType),
    [seats, seatType]
  );

  const priceLabel = useMemo(() => {
    if (!selectedSeat) return "";
    const price = selectedSeat.prices.find((p) => p.interval === interval);
    if (!price) return "Not available for this billing period";
    return formatSeatPriceLabel(price, selectedSeat.trial_days);
  }, [selectedSeat, interval]);

  // send the customer to Stripe. Keep the URLs stable (no cache-busting) so a
  // double-submit reuses the open checkout session rather than replacing it.
  async function proceedToCheckout(seat_type: string, billingInterval: Interval) {
    if (submitting) return;
    try {
      setSubmitting(true);
      setCheckoutError(null);
      const { checkout_url } = await checkoutSeatAsync({
        seat_type,
        interval: billingInterval,
        // No owned instance to bind to: omit msp_id so the backend provisions one.
        ...(mspId ? { msp_id: mspId } : {}),
        success_url: `${window.location.origin}/crystal/success`,
        cancel_url: `${window.location.origin}/crystal`,
      });
      window.location.replace(checkout_url);
    } catch (err) {
      // Every checkout refusal is a 400 carrying a customer-facing message; a
      // backend/Stripe hiccup surfaces the same way, so let the user retry.
      setCheckoutError(
        (err as Error).message || "Checkout failed. Please try again."
      );
      setSubmitting(false);
    }
  }

  function handleOrder() {
    proceedToCheckout(seatType, interval);
  }

  const orderDisabled = submitting || loadingQuote || !seatType;

  return (
    <div className={styles["page"]}>
      <div className={styles["order-box"]}>
        {ownerInstances.length > 1 && (
          <div className={styles["row"]}>
            <label className={styles["row-label"]} htmlFor="msp-instance">
              MSP Instance:
            </label>
            <select
              id="msp-instance"
              className={styles["select"]}
              value={mspId}
              onChange={(e) => setMspId(e.target.value)}
            >
              {ownerInstances.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.name || it.domain}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className={styles["row"]}>
          <label className={styles["row-label"]} htmlFor="crystal-type">
            Crystal Type:
          </label>
          <select
            id="crystal-type"
            className={styles["select"]}
            value={seatType}
            disabled={loadingQuote}
            onChange={(e) => setSeatType(e.target.value)}
          >
            {loadingQuote ? (
              <option value="">Loading…</option>
            ) : (
              seats.map((seat) => (
                <option key={seat.seat_type} value={seat.seat_type}>
                  {seatTypeLabel(seat.seat_type)}
                </option>
              ))
            )}
          </select>
        </div>

        <div className={styles["row"]}>
          <label className={styles["row-label"]} htmlFor="subscription">
            Subscription:
          </label>
          <select
            id="subscription"
            className={styles["select"]}
            value={interval}
            onChange={(e) => setInterval(e.target.value as Interval)}
          >
            {SUBSCRIPTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {quoteError ? (
          <p className={styles["price"]}>{quoteError}</p>
        ) : (
          <p className={styles["price"]}>{priceLabel}</p>
        )}

        <button
          className={styles["order-button"]}
          onClick={handleOrder}
          disabled={orderDisabled}
        >
          {submitting ? "Processing…" : "Order Now"}
        </button>

        {checkoutError && (
          <p className={styles["price"]} style={{ fontSize: 16, color: "#d00" }}>
            {checkoutError}
          </p>
        )}
      </div>
    </div>
  );
};

export default Crystal;
